<template>
	<div class="flex flex-col gap-2">
		<label v-if="label" class="text-sm font-medium">
			{{ label }}
		</label>
		<div class="h-96 w-full overflow-hidden rounded-lg border">
			<base-map ref="baseMapRef" height="100%">
				<template #controls>
					<!-- Hide default controls for preview -->
				</template>
			</base-map>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, watch, onUnmounted } from "vue";
	import type { Polygon, MultiPolygon } from "geojson";
	import { bbox } from "@turf/turf";
	import BaseMap from "@/components/ui/maps/BaseMap.vue";

	interface BoundaryPreviewMapProps {
		/** The boundary geometry to display */
		boundary: Polygon | MultiPolygon;
		/** Label text above the map */
		label?: string;
	}

	const props = withDefaults(defineProps<BoundaryPreviewMapProps>(), {
		label: "",
	});

	const baseMapRef = ref<InstanceType<typeof BaseMap> | null>(null);
	const hasAddedStyleListener = ref(false);

	function addBoundaryToMap() {
		const boundary = props.boundary;
		const map = baseMapRef.value?.map;

		if (!boundary || !map) {
			return;
		}

		const sourceId = "preview-boundary";
		const fillLayerId = "preview-boundary-fill";
		const outlineLayerId = "preview-boundary-outline";

		if (map.getLayer(fillLayerId)) {
			map.removeLayer(fillLayerId);
		}
		if (map.getLayer(outlineLayerId)) {
			map.removeLayer(outlineLayerId);
		}
		if (map.getSource(sourceId)) {
			map.removeSource(sourceId);
		}

		map.addSource(sourceId, {
			type: "geojson",
			data: {
				type: "Feature",
				geometry: boundary,
				properties: {},
			},
		});

		map.addLayer({
			id: fillLayerId,
			type: "fill",
			source: sourceId,
			paint: {
				"fill-color": "#3B82F6",
				"fill-opacity": 0.2,
			},
		});

		map.addLayer({
			id: outlineLayerId,
			type: "line",
			source: sourceId,
			paint: {
				"line-color": "#3B82F6",
				"line-width": 2,
			},
		});

		const [minLng, minLat, maxLng, maxLat] = bbox(boundary);
		map.fitBounds(
			[
				[minLng, minLat],
				[maxLng, maxLat],
			],
			{
				padding: 40,
				duration: 0,
			},
		);
	}

	watch(
		() => baseMapRef.value?.isInitialized,
		(initialized) => {
			if (initialized && !hasAddedStyleListener.value) {
				const map = baseMapRef.value?.map;
				if (map) {
					map.on("style.load", addBoundaryToMap);
					hasAddedStyleListener.value = true;
				}
			}
		},
		{ immediate: true },
	);

	watch(
		[() => props.boundary, () => baseMapRef.value?.isInitialized],
		([boundary, initialized]) => {
			if (boundary && initialized) {
				addBoundaryToMap();
			}
		},
		{ immediate: true },
	);

	onUnmounted(() => {
		const map = baseMapRef.value?.map;
		if (map) {
			map.off("style.load", addBoundaryToMap);
		}
	});
</script>
